/* screens/settings/BusinessSection.jsx — раздел «Для бизнеса» (window.BusinessSection).
   Источник бизнес-пространств — РЕАЛЬНЫЙ список из useAuth() (workspaces, kind==='business'),
   уже загруженный AuthProvider'ом (никакого отдельного fetch). Пустое состояние (нет бизнес-ws) →
   CTA «Создать» (BusinessUpgradeWizard). Есть ws → шапка (inline-rename через renameWorkspace +
   «Ещё заведение») + выбор ws (если >1) + подвкладки Тариф/Заведение/База знаний/Команда
   (deep-link &biz=, фикс №6; одноразовый ?sub=team|integrations из квеста онбординга →
   onBiz + сброс параметра). Раздел /restaurant убран — профиль и база знаний живут здесь.
   Все подвкладки получают РЕАЛЬНЫЙ wsId выбранного бизнес-пространства (X-Workspace-Id без переключения).
   ХЕНДОФФ → frontend/pages/settings/BusinessSection.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { EmptyState } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Segmented, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { authReal } from '../../lib/adapters/auth.js';
import { RR } from '../../lib/router.jsx';
import { BusinessIntegrationsTab } from './BusinessIntegrationsTab.jsx';
import { BusinessKnowledgeTab } from './BusinessKnowledgeTab.jsx';
import { BusinessRestaurantTab } from './BusinessRestaurantTab.jsx';
import { BusinessTariffPanel } from './BusinessTariffPanel.jsx';
import { BusinessUpgradeWizard } from './BusinessUpgradeWizard.jsx';
import { TeamTab } from './TeamTab.jsx';
import { resolveBusinessSubtab, visibleBusinessSubtabs } from './businessSubtabs.js';

  const { useState, useRef, useEffect } = React;
  const { useSearchParams } = RR;

  // Какое заведение открыть. Приоритет: явный выбор в списке → ?ws=<id> из адреса (письмо
  // и колокольчик об окончании подписки ведут в тариф ИМЕННО этого заведения) → активное
  // бизнес-пространство → первое. Чужой, несуществующий или мусорный id тихо игнорируется:
  // список — только доступные человеку пространства, а в нём такого id просто нет.
  function pickBusinessWorkspace(list, { selectedId = null, wsParam = null, activeId = null } = {}) {
    const items = list || [];
    const byId = (id) => (id == null ? undefined : items.find((w) => w.id === id));
    const raw = wsParam == null ? '' : String(wsParam).trim();
    const fromUrl = /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : null;
    return byId(selectedId) || byId(fromUrl) || byId(activeId) || items[0] || null;
  }

  // Состав подвкладок и правила их доступности — businessSubtabs.js (чистая логика, vitest):
  // «Интеграции» убирается, если тариф выбранного пространства закрыл раздел.

  function Inner({ list, selected, onSelect, onAddWorkspace, onRenamed, biz, onBiz, searchParams }) {
    const toast = useToast();
    const confirm = useConfirm();
    const [editing, setEditing] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [archiving, setArchiving] = useState(false);

    // Пространство подвкладок — ВЫБРАННОЕ (selected), не активное: настройки открывают любое
    // бизнес-пространство без переключения, а бэкенд гейтит интеграции по его id.
    // Закрытая тарифом подвкладка из URL (?biz=integrations из закладки) падает на «Тариф».
    const tabs = visibleBusinessSubtabs(selected);
    const sub = resolveBusinessSubtab(biz, selected);

    // data-onb якоря на кнопках подвкладок «Команда»/«Интеграции» — цели спотлайта
    // квеста онбординга. ui/Segmented не пробрасывает пропсы опций на кнопки (общий
    // UI-kit, вне скоупа этой задачи) — проставляем атрибуты на DOM после рендера;
    // реконсиляция React кнопки не пересоздаёт (стабильные key), атрибут живёт.
    const rootRef = useRef(null);
    useEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      const btns = root.querySelectorAll('.biz-subtabs .segmented__item');
      tabs.forEach((t, i) => {
        const onb = t.value === 'team' ? 'biz-sub-team'
          : t.value === 'integrations' ? 'biz-sub-integrations' : null;
        if (onb && btns[i]) btns[i].setAttribute('data-onb', onb);
      });
    }, [sub, tabs.length]);
    if (!selected) return null;
    const isOwner = !!(selected.membership && selected.membership.is_owner);
    // Менять тариф (checkout/смена/отмена) бэк разрешает только админам/владельцу ws;
    // не-админу прячем управление тарифом, чтобы не словить 403 (статус тарифа остаётся виден).
    const canManageTariff = !!(selected.membership && (selected.membership.is_admin || selected.membership.is_owner));

    const startRename = () => { setNameDraft(selected.name); setEditing(true); };
    const saveRename = async () => {
      const name = nameDraft.trim();
      if (!name || name === selected.name) { setEditing(false); return; }
      setSavingName(true);
      try {
        await authReal.renameWorkspace(selected.id, name);
        if (onRenamed) await onRenamed();
        setEditing(false);
        toast.show('Название обновлено', 'success');
      } catch (e) {
        toast.show(e && e.status === 403 ? 'Переименовать может только администратор' : ((e && e.detail) || 'Не удалось переименовать'), 'danger');
      } finally { setSavingName(false); }
    };

    const archive = async () => {
      const ok = await confirm({
        title: 'Архивировать пространство?',
        body: '«' + selected.name + '» исчезнет из списка, переключиться на него будет нельзя. Данные кухни сохранятся, но доступ к ним закроется.',
        confirmLabel: 'Архивировать', cancelLabel: 'Отмена', tone: 'danger',
      });
      if (!ok) return;
      setArchiving(true);
      try {
        await authReal.archiveWorkspace(selected.id);
        toast.show('Пространство архивировано', 'muted');
        onSelect(null);                      // сбросить выбор → фолбэк на первый оставшийся
        if (onRenamed) await onRenamed();    // onRenamed = refreshWorkspaces (список без архивного)
      } catch (e) {
        toast.show(e && e.code === 'not_owner' ? 'Архивировать может только владелец' : ((e && e.detail) || 'Не удалось архивировать'), 'danger');
      } finally { setArchiving(false); }
    };

    return (
      <div className="biz stack stack-16" ref={rootRef}>
        <div className="biz-head">
          <div className="biz-head__id">
            <span className="ws-switcher__avatar ws-switcher__avatar--business ws-switcher__avatar--sm" aria-hidden="true">{(selected.name || '?').slice(0, 1)}</span>
            {editing ? (
              <span className="biz-head__rename">
                <TextInput value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditing(false); }} />
                <Button variant="primary" size="sm" iconLeft="check" loading={savingName} disabled={savingName} onClick={saveRename}>ОК</Button>
                <Button variant="ghost" size="sm" disabled={savingName} onClick={() => setEditing(false)}>Отмена</Button>
              </span>
            ) : (
              <button type="button" className="biz-head__name" onClick={startRename}>
                <span className="t-h3">{selected.name}</span><Icon name="edit" size={15} />
              </button>
            )}
          </div>
          <Button variant="secondary" size="sm" iconLeft="plus" data-onb="biz-create"
            onClick={onAddWorkspace}>Ещё заведение</Button>
        </div>

        {list.length > 1 && (
          <div className="biz-select">
            <span className="t-caption text-mute">Пространство</span>
            <Select value={selected.id} onChange={onSelect} searchable={false} width={280}
              options={list.map((w) => ({ value: w.id, label: w.name, icon: 'building' }))} ariaLabel="Выбор бизнес-пространства" />
          </div>
        )}

        <Segmented value={sub} options={tabs} onChange={onBiz} ariaLabel="Подвкладки бизнеса" className="biz-subtabs" />

        <div className="biz-content">
          {sub === 'tariff' && <BusinessTariffPanel searchParams={searchParams} wsId={selected.id} canManage={canManageTariff} />}
          {sub === 'restaurant' && <BusinessRestaurantTab wsId={selected.id} />}
          {sub === 'knowledge' && <BusinessKnowledgeTab wsId={selected.id} />}
          {sub === 'team' && <TeamTab searchParams={searchParams} wsId={selected.id} />}
          {sub === 'integrations' && <BusinessIntegrationsTab wsId={selected.id} />}
        </div>

        {isOwner && (
          <section className="set-card set-card--danger-zone">
            <div className="set-card__head"><h3 className="t-h4">Архивация</h3><span className="t-caption text-mute">Только владелец</span></div>
            <div className="set-card__body stack stack-12">
              <p className="t-body-sm text-mute">Пространство исчезнет из списка и станет недоступным для переключения. Данные кухни не удаляются.</p>
              <Button variant="danger" size="sm" iconLeft="trash" loading={archiving} disabled={archiving} onClick={archive}>Архивировать пространство</Button>
            </div>
          </section>
        )}
      </div>
    );
  }

  function BusinessSection({ biz, onBiz, searchParams }) {
    const { workspaces, refreshWorkspaces, activeWorkspace } = useAuth();
    const toast = useToast();
    const [, setParams] = useSearchParams();
    const [wizardOpen, setWizardOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const createdIdRef = useRef(null);

    // deep-link ?create=1 (из переключателя пространства / меню пользователя) → авто-открытие
    // мастера создания бизнес-пространства; параметр сразу гасим, чтобы мастер не переоткрывался
    // при закрытии и повторном входе на вкладку.
    useEffect(() => {
      if (searchParams && searchParams.get('create')) {
        setWizardOpen(true);
        setParams((prev) => { const sp = new URLSearchParams(prev); sp.delete('create'); return sp; }, { replace: true });
      }
    }, [searchParams, setParams]);

    const businessList = (workspaces || []).filter((w) => w.kind === 'business');
    // дефолт — активное пространство, если оно бизнес (deep-link из чата ?doc=ID
    // должен попасть в базу знаний ТЕКУЩЕГО ws), иначе первое бизнес-ws; ?ws=<id> из
    // письма об окончании подписки открывает нужное заведение (параметр не стираем —
    // клик по другому заведению всё равно главнее)
    const selected = pickBusinessWorkspace(businessList, {
      selectedId,
      wsParam: searchParams ? searchParams.get('ws') : null,
      activeId: activeWorkspace ? activeWorkspace.id : null,
    });

    // deep-link ?sub=team|integrations (квест онбординга ведёт в конкретную подвкладку
    // «Для бизнеса») → открываем её через onBiz (тот же канал, что клик по Segmented:
    // родитель пишет &biz=) и гасим параметр — тот же паттерн, что ?create выше.
    // Подвкладку, закрытую тарифом выбранного пространства («Интеграции»), НЕ открываем:
    // параметр просто гасим — остаётся подвкладка по умолчанию, без пустой панели.
    useEffect(() => {
      if (!searchParams) return;
      const subParam = searchParams.get('sub');
      if (!subParam) return;
      if (visibleBusinessSubtabs(selected).some((t) => t.value === subParam)) {
        // Атомарно: onBiz писал &biz= отдельным setParams, и последующий сброс
        // sub затирал его стейл-снапшотом (гонка двух записей URL — смоук W3:
        // вкладка оставалась «Тариф»). Пишем biz и гасим sub одной операцией.
        setParams((prev) => {
          const sp = new URLSearchParams(prev);
          sp.set('biz', subParam);
          sp.delete('sub');
          return sp;
        }, { replace: true });
      } else if (subParam === 'integrations') {
        setParams((prev) => { const sp = new URLSearchParams(prev); sp.delete('sub'); return sp; }, { replace: true });
      }
    }, [searchParams, setParams, onBiz, selected]);

    // create фиксирует id; рефреш + выбор нового ws — на закрытии мастера (модалка живёт через шаг оплаты)
    const handleCreated = (created) => { createdIdRef.current = created && created.id; };
    const handleWizardClose = async () => {
      setWizardOpen(false);
      const created = createdIdRef.current; createdIdRef.current = null;
      try { await refreshWorkspaces(); } catch (e) { /* список обновится при следующем bootstrap */ }
      if (created) setSelectedId(created);
    };

    return (
      <div className="settings-panel">
        {businessList.length === 0 ? (
          <div className="biz-empty">
            <EmptyState icon="building" title="Создайте бизнес-пространство"
              body="Командный доступ с ролями, общая корзина сообщений и аудио, профиль заведения и база знаний для всей команды. Тарифы Business S / M / L — выбираете при создании."
              action={<Button variant="primary" iconLeft="plus" data-onb="biz-create"
                onClick={() => setWizardOpen(true)}>Создать бизнес-пространство</Button>} />
          </div>
        ) : (
          <Inner list={businessList} selected={selected} onSelect={setSelectedId}
            onAddWorkspace={() => setWizardOpen(true)} onRenamed={refreshWorkspaces}
            biz={biz} onBiz={onBiz} searchParams={searchParams} />
        )}
        <BusinessUpgradeWizard open={wizardOpen} onClose={handleWizardClose} onCreated={handleCreated} />
      </div>
    );
  }

export { BusinessSection };
export { pickBusinessWorkspace };
